import { createContext, useCallback, useContext, useEffect, useState } from "react";
import { adminLogin as apiLogin, adminLogout as apiLogout, adminMe } from "@/lib/adminApi";

const AdminAuthContext = createContext(null);

/**
 * Auth states:
 *   user === undefined : initial / checking
 *   user === null      : not authenticated
 *   user === object    : authenticated admin
 */
export function AdminAuthProvider({ children }) {
  const [user, setUser] = useState(undefined);

  const refresh = useCallback(async () => {
    // Only probe the session on admin routes — keeps the public marketing
    // pages' browser console free of benign 401s for logged-out visitors.
    if (typeof window !== "undefined" && !window.location.pathname.startsWith("/admin")) {
      setUser(null);
      return null;
    }
    try {
      const me = await adminMe();
      setUser(me);
      return me;
    } catch {
      setUser(null);
      return null;
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const login = useCallback(async (email, password) => {
    const res = await apiLogin(email, password);
    setUser(res.user);
    return res.user;
  }, []);

  const logout = useCallback(async () => {
    try {
      await apiLogout();
    } finally {
      setUser(null);
    }
  }, []);

  return (
    <AdminAuthContext.Provider value={{ user, login, logout, refresh }}>
      {children}
    </AdminAuthContext.Provider>
  );
}

export function useAdminAuth() {
  const ctx = useContext(AdminAuthContext);
  if (!ctx) throw new Error("useAdminAuth must be used inside AdminAuthProvider");
  return ctx;
}
